import { defineStore } from 'pinia';
import { collection, getDocs, addDoc, query, where, orderBy, limit } from 'firebase/firestore';
import type { Activity } from '~/types/models';

function activityTime(value: unknown): number {
    if (!value) return 0;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
        return (value as { toDate: () => Date }).toDate().getTime();
    }
    if (typeof value === 'object' && value !== null && 'seconds' in value) {
        return Number((value as { seconds: number }).seconds) * 1000;
    }

    const parsed = new Date(String(value)).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
}

const stripUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== undefined)
    ) as Partial<T>;
};

/**
 * Activity Store
 * 
 * Manages activity logging and retrieval for audit trails and activity feeds.
 * Tracks all user actions across tasks, projects, and other entities.
 * 
 * Features:
 * - Log activities for create/update/delete/comment actions
 * - Retrieve recent activities
 * - Filter activities by entity type
 * - Pagination support
 * - Audit trail generation
 * 
 * Design Patterns:
 * - Activities are immutable once created
 * - All activities include user context (ID, name, avatar)
 * - Field-level tracking for updates (previous/new values)
 * - Tenant isolation via tenantId
 * 
 * Usage:
 * // Log a task creation
 * await activityStore.logActivity({
 *   action: 'create',
 *   entityType: 'task',
 *   entityId: 'task-123',
 *   description: 'Created task: Build login page',
 *   userId: 'user-456',
 *   userName: 'John Doe'
 * })
 * 
 * // Get recent activities
 * const activities = await activityStore.fetchRecentActivities(10)
 * 
 * // Get activities for specific entity
 * const taskActivities = await activityStore.fetchActivitiesByEntity('task', 'task-123')
 */
export const useActivityStore = defineStore('activity', {
    state: () => ({
        activities: [] as Activity[],
        activitiesByEntity: {} as Record<string, Activity[]>,
        activityError: null as string | null,
    }),

    actions: {
        /**
         * Gets the current tenant ID from the tenant store
         * @private
         */
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        /**
         * Log an activity to the database
         * 
         * Creates a new activity record for audit trail and activity feeds.
         * All activities are immutable once created.
         * 
         * @param activity - Activity data (without id and createdAt)
         * @returns The ID of the created activity
         */
        async logActivity(
            activity: Omit<Activity, 'id' | 'createdAt' | 'tenantId'>
        ): Promise<string> {
            const { $db } = useNuxtApp();
            this.activityError = null;

            try {
                const tenantId = this._getTenantId();
                const collectionRef = collection($db, `tenants/${tenantId}/activities`);

                const activityData = stripUndefined({
                    ...activity,
                    tenantId,
                    createdAt: new Date(),
                });

                const docRef = await addDoc(collectionRef, activityData);
                
                // Add to local state
                this.activities.push({ 
                    id: docRef.id, 
                    ...activityData 
                } as Activity);
                
                return docRef.id;
            } catch (error: any) {
                this.activityError = error.message;
                console.error('Error logging activity:', error);
                throw error;
            }
        },

        /**
         * Fetch recent activities (most recent first)
         * 
         * @param maxResults - Maximum number of results (default 20)
         * @returns Promise<Activity[]>
         */
        async fetchRecentActivities(maxResults: number = 20): Promise<Activity[]> {
            const { $db } = useNuxtApp();
            this.activityError = null;

            try {
                const tenantId = this._getTenantId();
                const collectionRef = collection($db, `tenants/${tenantId}/activities`);
                const q = query(
                    collectionRef,
                    orderBy('createdAt', 'desc'),
                    limit(maxResults)
                );
                
                const snap = await getDocs(q);
                const activities = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as Activity));

                this.activities = activities;
                return activities;
            } catch (error: any) {
                this.activityError = error.message;
                console.error('Error fetching activities:', error);
                return [];
            }
        },

        /**
         * Fetch activities for a specific entity
         * 
         * @param entityType - Type of entity (task, project, client, team)
         * @param entityId - ID of the entity
         * @param maxResults - Maximum number of results (default 50)
         * @returns Promise<Activity[]>
         */
        async fetchActivitiesByEntity(
            entityType: Activity['entityType'],
            entityId: string,
            maxResults: number = 50
        ): Promise<Activity[]> {
            const { $db } = useNuxtApp();
            this.activityError = null;

            try {
                const tenantId = this._getTenantId();
                const collectionRef = collection($db, `tenants/${tenantId}/activities`);
                const q = query(
                    collectionRef,
                    where('entityType', '==', entityType),
                    where('entityId', '==', entityId)
                );
                
                const snap = await getDocs(q);
                const activities = snap.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    } as Activity))
                    .sort((a, b) => activityTime(b.createdAt) - activityTime(a.createdAt))
                    .slice(0, maxResults);
                this.activitiesByEntity[`${entityType}:${entityId}`] = activities;
                return activities;
            } catch (error: any) {
                this.activityError = error.message;
                console.error('Error fetching entity activities:', error);
                this.activitiesByEntity[`${entityType}:${entityId}`] = [];
                return [];
            }
        },

        /**
         * Fetch activities by user
         * Useful for "user's activity" feeds
         * 
         * @param userId - ID of the user
         * @param maxResults - Maximum number of results (default 50)
         * @returns Promise<Activity[]>
         */
        async fetchActivitiesByUser(
            userId: string,
            maxResults: number = 50
        ): Promise<Activity[]> {
            const { $db } = useNuxtApp();
            this.activityError = null;

            try {
                const tenantId = this._getTenantId();
                const collectionRef = collection($db, `tenants/${tenantId}/activities`);
                const q = query(
                    collectionRef,
                    where('userId', '==', userId),
                    orderBy('createdAt', 'desc'),
                    limit(maxResults)
                );
                
                const snap = await getDocs(q);
                return snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as Activity));
            } catch (error: any) {
                this.activityError = error.message;
                console.error('Error fetching user activities:', error);
                return [];
            }
        },

        /**
         * Fetch activities by action type
         * 
         * @param action - Action type (create, update, delete, comment)
         * @param maxResults - Maximum number of results (default 50)
         * @returns Promise<Activity[]>
         */
        async fetchActivitiesByAction(
            action: Activity['action'],
            maxResults: number = 50
        ): Promise<Activity[]> {
            const { $db } = useNuxtApp();
            this.activityError = null;

            try {
                const tenantId = this._getTenantId();
                const collectionRef = collection($db, `tenants/${tenantId}/activities`);
                const q = query(
                    collectionRef,
                    where('action', '==', action),
                    orderBy('createdAt', 'desc'),
                    limit(maxResults)
                );
                
                const snap = await getDocs(q);
                return snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as Activity));
            } catch (error: any) {
                this.activityError = error.message;
                console.error('Error fetching action activities:', error);
                return [];
            }
        }
    }
});
